/*
 * moasure-2-pro-runtime.js
 * Native replacement for Instant's `instant-core.min.js` on the Moasure 2 PRO PDP.
 * Reads the SAME `data-instant-*` attributes the Instant markup already carries and drives the
 * SAME state attributes/classes the existing CSS responds to. Defensive throughout (no throws),
 * and fully idempotent (a global + per-element guard prevent any double-binding).
 */
(function () {
  'use strict';
  if (window.__mz2proInit) return;              // global idempotency guard
  window.__mz2proInit = true;
  var ROOT_SEL = '.__instant';

  function $all(sel, ctx) { try { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); } catch (e) { return []; } }
  function safe(fn) { return function () { try { return fn.apply(this, arguments); } catch (e) { /* swallow */ } }; }
  function closest(el, sel) { while (el && el.nodeType === 1) { if (el.matches && el.matches(sel)) return el; el = el.parentElement; } return null; }
  function bound(el) { if (!el || el.__mzBound) return true; el.__mzBound = true; return false; }

  /* ---------------- TABS ---------------- */
  function initTabs(root) {
    $all('[data-instant-type="tabs-container"]', root).forEach(function (container) {
      var content = $all('[data-instant-type="tabs-content"]', container).filter(function (c) { return closest(c, '[data-instant-type="tabs-container"]') === container; })[0];
      var triggers = $all('[data-instant-type="tabs-trigger"]', container).filter(function (t) { return closest(t, '[data-instant-type="tabs-container"]') === container; });
      if (!content || !triggers.length) return;
      var panes = $all(':scope > [data-instant-type="tabs-pane"]', content);
      function activate(tabId) {
        triggers.forEach(function (t) { t.setAttribute('data-instant-state', t.getAttribute('data-instant-tab-id') === tabId ? 'active' : 'inactive'); });
        panes.forEach(function (p) { p.classList.toggle('mz-tab-on', p.id === tabId); });
      }
      triggers.forEach(function (t) { if (bound(t)) return; t.addEventListener('click', safe(function (e) { e.preventDefault(); activate(t.getAttribute('data-instant-tab-id')); })); });
      // match instant-core: the FIRST PANE decides the default tab, not the
      // first trigger — so a page that reorders panes gets the same default
      // from either runtime.
      activate(panes[0] && panes[0].id ? panes[0].id : triggers[0].getAttribute('data-instant-tab-id'));   // initial active = first tab
    });
  }

  /* ---------------- ACCORDIONS ---------------- */
  function setAcc(item, open) {
    var content = item.querySelector('[data-instant-type="accordion-content"]');
    if (content) content.style.setProperty('--instant-accordion-content-height', open ? content.scrollHeight + 'px' : '0px');
    item.setAttribute('data-state', open ? 'open' : 'closed');
  }
  function initAccordions(root) {
    $all('[data-instant-type="accordion-header"]', root).forEach(function (header) {
      if (bound(header)) return;
      header.addEventListener('click', safe(function (e) {
        e.preventDefault(); e.stopPropagation();
        var item = closest(header, '[data-instant-type="accordion-item"]');
        if (!item) return;
        var container = closest(item, '[data-instant-type="accordion-container"]');
        var multi = container && container.getAttribute('data-is-multi-open-enabled') === 'true';
        var isOpen = item.getAttribute('data-state') === 'open';
        if (!multi && container) $all('[data-instant-type="accordion-item"]', container).forEach(function (o) { if (o !== item && closest(o, '[data-instant-type="accordion-container"]') === container) setAcc(o, false); });
        setAcc(item, !isOpen);
      }));
    });
  }

  /* ---------------- OVERLAYS ---------------- */
  function overlayFor(id) { return document.getElementById('i' + id) || document.getElementById(id); }
  function openOverlay(o) { if (!o) return; o.setAttribute('data-state', 'open'); document.documentElement.style.overflow = 'hidden'; }
  function closeOverlay(o) { if (!o) return; o.setAttribute('data-state', 'closed'); document.documentElement.style.overflow = ''; $all('video', o).forEach(function (v) { try { v.pause(); } catch (e) {} }); }
  function initOverlays(root) {
    $all('[data-instant-action-type="open-overlay"]', root).forEach(function (btn) {
      if (bound(btn)) return;
      btn.addEventListener('click', safe(function (e) {
        var o = overlayFor(btn.getAttribute('data-instant-action-id'));
        if (!o) return;
        e.preventDefault();
        if (closest(btn, '[data-instant-type="overlay"]') === o && o.getAttribute('data-state') === 'open') closeOverlay(o); else openOverlay(o);
      }));
    });
    $all('[data-instant-type="overlay"]', root).forEach(function (o) {
      if (bound(o)) return;
      o.addEventListener('click', safe(function (e) { if (e.target === o) closeOverlay(o); }));
      $all('[class*="close"],[aria-label*="lose"],[data-instant-action-type="close-overlay"]', o).forEach(function (c) { c.addEventListener('click', safe(function () { closeOverlay(o); })); });
    });
    if (!window.__mz2proEsc) { window.__mz2proEsc = true; document.addEventListener('keydown', safe(function (e) { if (e.key !== 'Escape') return; var open = $all('[data-instant-type="overlay"][data-state="open"]'); if (open.length) closeOverlay(open[open.length - 1]); })); }
  }

  /* ---------------- SLIDERS ---------------- */
  function readParams(slider) { try { var s = document.getElementById('instant-slider-' + slider.id + '-params'); return s ? JSON.parse(s.textContent) : null; } catch (e) { return null; } }
  function bp(params, key) {
    if (!params || !params.breakpoints) return undefined;
    var w = window.innerWidth, best, bestK = -1;
    for (var k in params.breakpoints) { var n = parseInt(k, 10); if (w >= n && n > bestK) { bestK = n; best = params.breakpoints[k]; } }
    return best ? best[key] : undefined;
  }
  function initSliders(root) {
    $all('[data-instant-type="slider"]', root).forEach(function (slider) {
      if (bound(slider)) return;
      var wrapper = slider.querySelector('.instant-slider-wrapper');
      if (!wrapper) return;
      var slides = $all(':scope > .instant-slider-slide', wrapper);
      if (!slides.length) return;
      var params = readParams(slider);
      var centered = bp(params, 'centeredSlides');
      var sid = slider.getAttribute('data-instant-slider-id');
      var container = closest(slider, '.instant-slider-container') || slider.parentElement;
      var idx = 0;
      function offsetFor(i) {
        var s = slides[i]; if (!s) return 0;
        var base = s.offsetLeft - slides[0].offsetLeft;
        if (centered) base -= (slider.clientWidth - s.clientWidth) / 2;
        return Math.max(0, Math.min(base, Math.max(0, wrapper.scrollWidth - slider.clientWidth)));
      }
      function go(i) {
        idx = Math.max(0, Math.min(i, slides.length - 1));
        wrapper.style.transitionDuration = '300ms';
        wrapper.style.transform = 'translate3d(' + (-offsetFor(idx)) + 'px,0,0)';
        slider.setAttribute('data-active-index', String(idx));
        slides.forEach(function (s, n) { s.classList.toggle('instant-slider-slide-active', n === idx); });
        updateBullets(); linkThumbs();
      }
      var nav = (params && params.navigation) || {};
      wireNav(container, nav.nextEl, function () { go(idx + 1); });
      wireNav(container, nav.prevEl, function () { go(idx - 1); });
      var pagEl = params && params.pagination && params.pagination.el ? (container.querySelector(params.pagination.el) || document.querySelector(params.pagination.el)) : null;
      var bullets = [];
      if (pagEl) {
        if (pagEl.children.length === slides.length) bullets = $all(':scope > *', pagEl);
        else { pagEl.innerHTML = ''; slides.forEach(function () { var x = document.createElement('span'); x.className = 'instant-slider-pagination-bullet'; pagEl.appendChild(x); bullets.push(x); }); }
        bullets.forEach(function (x, n) { if (!bound(x)) x.addEventListener('click', safe(function () { go(n); })); });
      }
      function updateBullets() { bullets.forEach(function (x, n) { x.classList.toggle('instant-slider-pagination-bullet-active', n === idx); x.classList.toggle('instant-slider-pagination-bullet-active-main', n === idx); }); }
      function linkThumbs() { if (!sid) return; $all('[data-instant-type="thumbnails"][data-instant-slider-id="' + sid + '"] .instant-slider-slide', root).forEach(function (t, n) { t.classList.toggle('instant-slider-slide-active', n === idx); }); }
      if (sid) $all('[data-instant-type="thumbnails"][data-instant-slider-id="' + sid + '"] .instant-slider-slide', root).forEach(function (t, n) { if (!bound(t)) t.addEventListener('click', safe(function () { go(n); })); });
      window.addEventListener('resize', safe(function () { go(idx); }));
      go(0);
    });
  }
  function wireNav(ctx, sel, fn) {
    if (!sel) return;
    var els = $all(sel, ctx); if (!els.length) els = $all(sel, document);
    els.forEach(function (el) { if (bound(el)) return; el.addEventListener('click', safe(function (e) { e.preventDefault(); fn(); })); });
  }

  /* ---------------- TICKER ---------------- */
  function initTicker(root) {
    $all('[data-instant-type="ticker"]', root).forEach(function (t) {
      var anim = t.querySelector('.instant-ticker') || t;
      var cs = getComputedStyle(anim);
      if (!cs.getPropertyValue('--duration')) anim.style.setProperty('--duration', '30s');
      if (!String(cs.getPropertyValue('--play')).trim()) anim.style.setProperty('--play', 'running');
      if (!String(cs.getPropertyValue('--direction')).trim()) anim.style.setProperty('--direction', 'normal');
      anim.style.setProperty('--pause-on-hover', 'paused');
      anim.style.setProperty('--pause-on-click', 'paused');
    });
  }

  /* ---------------- SCROLL-TO ---------------- */
  function initScrollTo(root) {
    $all('[data-instant-action-type="scroll-to-section"]', root).forEach(function (el) {
      if (bound(el)) return;
      el.addEventListener('click', safe(function (e) {
        var id = el.getAttribute('data-instant-action-id') || (el.getAttribute('href') || '').replace('#', '');
        var target = document.getElementById('i' + id) || document.getElementById(id);
        if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      }));
    });
  }

  // While the Instant app embed is still installed store-wide, instant-core.min.js auto-initializes
  // this section's `.__instant` markup (identically to the live source). Running our native runtime
  // too would double-bind and fight it. So we STAND DOWN whenever instant-core is present, and only
  // take over once the Instant app is removed (the section's future self-contained state).
  function instantCorePresent() {
    if (window.Instant && (window.Instant.initialized || window.Instant.initializedAppEmbed)) return true;
    return !!document.querySelector('script[src*="instant-core"]');
  }
  function init() {
    if (instantCorePresent()) return;
    // we are driving the section ourselves — install the tab-pane reveal CSS our tab logic relies on
    try { var st = document.createElement('style'); st.textContent = '[data-instant-type="tabs-content"] > [data-instant-type="tabs-pane"]:not(.mz-tab-on){display:none !important}'; document.head.appendChild(st); } catch (e) {}
    $all(ROOT_SEL).forEach(function (root) {
      safe(initTabs)(root); safe(initAccordions)(root); safe(initOverlays)(root);
      safe(initSliders)(root); safe(initTicker)(root); safe(initScrollTo)(root);
    });
    window.__mz2proReady = true; // signals the native runtime is driving + ready
  }
  // run a touch later so a deferred/async instant-core injection is detectable before we decide
  function boot() { setTimeout(init, 50); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
